import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  RefreshCw,
  Activity,
  Trophy,
  AlertOctagon,
  Users,
  Building2,
  MapPin,
  ClipboardCheck,
  ChevronRight,
  Shield,
  Sparkles
} from 'lucide-react';
import PageMeta from '../../shared/PageMeta';
import {
  dashboardService,
  type SuperAdminOverview,
  type SuperAdminBucket
} from '../../services/dashboardService';
import BucketCard from './superAdmin/BucketCard';
import ComplianceHeatmap from './superAdmin/ComplianceHeatmap';
import PipelineFunnel from './superAdmin/PipelineFunnel';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const sectionVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const SEVERITY_STYLES = {
  info: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  critical: 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
} as const;

const RISK_BADGES = {
  low: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20',
  medium: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20',
  high: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 ring-orange-500/20',
  critical: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20'
} as const;

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<SuperAdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const res = await dashboardService.getSuperAdminOverview();
      setData(res);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      console.error('SuperAdmin overview load error:', err);
      setError(message);
      if (showLoading) toast.error(`Failed to load dashboard: ${message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData(true);
    const interval = setInterval(() => loadData(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalsTiles = useMemo(() => {
    if (!data) return [];
    return [
      { icon: Building2, label: 'Total Projects', value: data.totals.totalProjects, accent: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500/10' },
      { icon: Users, label: 'Active Users (7d)', value: data.totals.activeUsers, accent: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10' },
      { icon: MapPin, label: 'Sites', value: data.totals.totalSites, accent: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-500/10' },
      { icon: ClipboardCheck, label: 'Pending Approvals', value: data.totals.pendingApprovals, accent: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' }
    ];
  }, [data]);

  const handleBucketClick = (bucket: SuperAdminBucket) => {
    navigate(`/dashboard/super-admin/portfolio?bucket=${bucket}`);
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
          <Sparkles className="h-8 w-8 animate-pulse text-violet-500" />
          <p className="text-sm">Loading SuperAdmin dashboard…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
          <AlertOctagon className="h-8 w-8 text-rose-500" />
          <p className="text-sm">{error ?? 'No dashboard data available'}</p>
          <button
            onClick={() => loadData(true)}
            className="mt-2 rounded-lg bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const lastUpdated = new Date(data.lastUpdated);

  return (
    <>
      <PageMeta title="SuperAdmin Dashboard" description="Org-wide project, user & reporting overview" />

      <div className="flex flex-col gap-6">
        {/* HEADER */}
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-slate-200/80 bg-gradient-to-br from-violet-500/10 via-white to-blue-500/10 p-5 shadow-sm dark:border-slate-700/50 dark:from-violet-900/20 dark:via-slate-800/60 dark:to-blue-900/20"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-lg">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">
                SuperAdmin · Command Center
              </p>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Org-wide Project Health
              </h1>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ·
                {' '}Auto-refresh every 5 min
              </p>
            </div>
          </div>

          <button
            onClick={() => loadData(false)}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-violet-300 hover:text-violet-600 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-violet-500"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </motion.div>

        {/* HERO BUCKETS */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {data.buckets.map((kpi, i) => (
            <BucketCard key={kpi.bucket} kpi={kpi} index={i} onClick={handleBucketClick} />
          ))}
        </div>

        {/* TOTALS STRIP */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {totalsTiles.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={t.label}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.1 + i * 0.04 }}
                className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
              >
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${t.bg}`}>
                  <Icon className={`h-5 w-5 ${t.accent}`} />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t.label}
                  </p>
                  <p className="text-xl font-bold tabular-nums text-slate-900 dark:text-white">
                    {t.value.toLocaleString()}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ROW: Pipeline + Heatmap */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <PipelineFunnel stages={data.pipelineFunnel} />
          </div>
          <div className="lg:col-span-2">
            <ComplianceHeatmap
              dates={data.complianceHeatmap.dates}
              rows={data.complianceHeatmap.rows}
              onRowClick={(pid) => navigate(`/dashboard/super-admin/projects/${pid}`)}
            />
          </div>
        </div>

        {/* ROW: Lagging projects + Top performers */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
          >
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-500/10">
                <AlertOctagon className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Top Lagging Projects
                </h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Lowest health score · click to deep-dive
                </p>
              </div>
            </div>
            {data.laggingProjects.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-slate-400">
                No at-risk projects right now
              </div>
            ) : (
              <ul className="space-y-2">
                {data.laggingProjects.map(p => (
                  <li key={p.projectId}>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/super-admin/projects/${p.projectId}`)}
                      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-slate-50/40 px-3 py-2 transition hover:border-rose-300 hover:bg-white dark:border-slate-700/50 dark:bg-slate-900/30 dark:hover:bg-slate-900"
                    >
                      <div className="min-w-0 text-left">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                          {p.projectName}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          {p.code} · {p.manager} · {p.daysSinceUpdate ?? '—'}d since update
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${RISK_BADGES[p.riskLevel]}`}>
                          {p.riskLevel}
                        </span>
                        <span className="text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                          {p.healthScore}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-rose-500" />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          <motion.div
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
          >
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10">
                <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Top Performers (7d)
                </h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Best engineers by completion & pass-rate
                </p>
              </div>
            </div>
            {data.topPerformers.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-slate-400">
                No activity in the last 7 days
              </div>
            ) : (
              <ul className="space-y-2">
                {data.topPerformers.map((u, i) => (
                  <li key={u.userId}>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/super-admin/users/${u.userId}`)}
                      className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-slate-50 dark:hover:bg-slate-700/40"
                    >
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                        i === 0 ? 'bg-amber-400 text-white' :
                        i === 1 ? 'bg-slate-300 text-slate-800' :
                        i === 2 ? 'bg-orange-400 text-white' :
                        'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {i + 1}
                      </span>
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-blue-400 text-xs font-semibold text-white">
                        {u.name.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                          {u.name}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          {u.role} · {u.completedTasks} done · {u.projectsCount} projects
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                          <div
                            className="h-full rounded-full bg-emerald-500"
                            style={{ width: `${u.score}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold tabular-nums text-slate-900 dark:text-white">{u.score}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>

        {/* ROW: Role performance + Activity feed */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
          >
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Role Performance</h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Avg score & completed tasks this week, by role
                </p>
              </div>
            </div>
            {data.rolePerformance.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-slate-400">
                No role data
              </div>
            ) : (
              <ul className="space-y-3">
                {data.rolePerformance.map(r => (
                  <li key={r.role}>
                    <div className="mb-1 flex items-baseline justify-between">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">{r.role}</span>
                        <span className="text-[10px] text-slate-500">{r.activeUsers}/{r.totalUsers} active</span>
                      </div>
                      <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
                        <span className="font-bold text-slate-900 dark:text-white">{r.avgScore}</span>/100 ·
                        <span className="ml-1">{r.completedThisWeek} done</span>
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/40">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${r.avgScore}%` }}
                        transition={{ duration: 0.6 }}
                        className={`h-full rounded-full ${
                          r.avgScore >= 75 ? 'bg-emerald-500'
                          : r.avgScore >= 50 ? 'bg-teal-500'
                          : r.avgScore >= 30 ? 'bg-amber-500'
                          : 'bg-rose-500'
                        }`}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          <motion.div
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
          >
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10">
                <Activity className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Live Activity</h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Latest task & checkpoint events
                </p>
              </div>
            </div>
            {data.activityFeed.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-slate-400">
                No recent activity
              </div>
            ) : (
              <ul className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                {data.activityFeed.map(a => (
                  <li
                    key={a.id}
                    className="flex items-start gap-3 rounded-xl border border-transparent bg-slate-50/40 px-3 py-2 transition hover:border-slate-200 dark:bg-slate-900/30 dark:hover:border-slate-700"
                  >
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${SEVERITY_STYLES[a.severity]}`}>
                      <Activity className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                        {a.message}
                      </p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">
                        {a.actor} · {a.relativeTime}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
